import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft, Laptop, Loader2, LogOut, Monitor, ShieldCheck, Smartphone, Tablet, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MemberLockScreen } from "@/components/MemberLockScreen";
import { useMembership } from "@/hooks/useMembership";
import { supabase } from "@/integrations/supabase/client";
import { DEVICE_LIMIT, getDeviceId, lastActiveLabel } from "@/lib/device-id";
import { listMyDevices, removeMyDevice, signOutOtherDevices, type DevicesState } from "@/lib/devices.functions";

export const Route = createFileRoute("/account/devices")({
  ssr: false,
  head: () => ({ meta: [
    { title: "Security & Devices — Recovery Trauma™" },
    { name: "description", content: "See where your Recovery Trauma™ account is signed in and sign out of devices you no longer use." },
    { property: "og:title", content: "Security & Devices — Recovery Trauma™" },
    { property: "og:description", content: "See where your Recovery Trauma™ account is signed in and sign out of devices you no longer use." },
    { name: "robots", content: "noindex, nofollow" }, { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" },
  ] }),
  component: DevicesRoute,
});

function DevicesRoute() {
  const membership = useMembership();
  if (membership.loading) {
    return <main className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[var(--gold-deep)]" /></main>;
  }
  if (!membership.isAuthed) return <MemberLockScreen areaName="Security & Devices" />;
  return <DevicesPage />;
}

function iconFor(platform: string | null) {
  if (platform === "ios" || platform === "android") return Smartphone;
  if (platform === "ipados") return Tablet;
  if (platform === "windows" || platform === "macos" || platform === "linux" || platform === "chromeos") return Monitor;
  return Laptop;
}

function DevicesPage() {
  const navigate = useNavigate();
  const load = useServerFn(listMyDevices);
  const remove = useServerFn(removeMyDevice);
  const signOutOthers = useServerFn(signOutOtherDevices);
  const [state, setState] = useState<DevicesState | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [confirmReport, setConfirmReport] = useState(false);
  const deviceId = getDeviceId();

  const refresh = useCallback(async () => {
    setState(await load({ data: { deviceId } }));
  }, [load, deviceId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const onRemove = async (id: string, label: string) => {
    if (!window.confirm(`Remove ${label}? It will be signed out. Your membership, journals and progress are not affected.`)) return;
    setBusy(true);
    try { setState(await remove({ data: { id, currentDeviceId: deviceId } })); setMessage(`${label} has been removed.`); }
    finally { setBusy(false); }
  };

  const onSignOutOthers = async (reason: "member_request" | "reported_access") => {
    setBusy(true);
    try {
      setState(await signOutOthers({ data: { keepDeviceId: deviceId, reason } }));
      setMessage(reason === "reported_access"
        ? "Every other device has been signed out. Changing your password next is a good next step — you're in control of when."
        : "Every other device has been signed out. This device stays signed in.");
      setConfirmReport(false);
    } finally { setBusy(false); }
  };

  const onSignOut = async () => {
    await supabase.auth.signOut();
    void navigate({ to: "/" });
  };

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
      <Link to="/my-membership" className="mb-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to my account
      </Link>

      <header>
        <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs text-[var(--gold-deep)]">
          <ShieldCheck className="h-4 w-4" /> Private account security
        </span>
        <h1 className="mt-4 font-display text-3xl text-foreground sm:text-4xl">Security &amp; Devices</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Your membership is personal to you, and can be used on up to {DEVICE_LIMIT} of your own
          devices — your phone, tablet and computer. Your journals, reflections and journey work
          are private to your account.
        </p>
      </header>

      {state?.sharingNotice && (
        <section className="mt-6 rounded-lg border border-gold/30 bg-gold/10 p-4">
          <h2 className="font-display text-base text-foreground">We've noticed your account may be being used on multiple devices.</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Your Recovery Trauma™ membership is personal and shouldn't be shared with other
            people. You can review and remove devices below.
          </p>
        </section>
      )}

      <section className="mt-7">
        <h2 className="font-display text-xl">Your Devices</h2>
        {!state ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading your devices…</p>
        ) : state.devices.length === 0 ? (
          <p className="mt-3 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">No devices recorded yet.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {state.devices.map((device) => {
              const Icon = iconFor(device.platform);
              return (
                <div key={device.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
                  <Icon className="h-5 w-5 shrink-0 text-[var(--gold-deep)]" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {device.label}{device.isCurrent && <span className="ms-2 text-xs text-[var(--gold-deep)]">This device</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{lastActiveLabel(device.lastSeenAt)}</p>
                  </div>
                  {!device.isCurrent && (
                    <Button variant="ghost" size="icon" className="min-h-11 min-w-11" disabled={busy}
                      aria-label={`Remove ${device.label}`} onClick={() => void onRemove(device.id, device.label)}>
                      <Trash2 />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          {state ? `${state.devices.length} of ${DEVICE_LIMIT} devices in use.` : ""}
        </p>
      </section>

      {message && <p role="status" className="mt-4 rounded-lg border border-gold/20 bg-gold/10 p-3 text-sm text-foreground">{message}</p>}

      <section className="mt-7 space-y-3">
        <Button type="button" variant="outline" className="min-h-12 w-full" onClick={() => void onSignOut()}>
          <LogOut /> Sign Out
        </Button>
        <Button type="button" className="min-h-12 w-full" disabled={busy} onClick={() => void onSignOutOthers("member_request")}>
          <ShieldCheck /> Sign Out of All Other Devices
        </Button>
      </section>

      <section className="mt-8 rounded-lg border border-border bg-card p-5">
        <h2 className="font-display text-lg">Someone else has accessed my account</h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          If that's happened, we'll sign out every other device straight away. You can then
          change your password whenever you feel ready.
        </p>
        {confirmReport ? (
          <div className="mt-4 space-y-2">
            <Button type="button" className="min-h-12 w-full" disabled={busy} onClick={() => void onSignOutOthers("reported_access")}>
              Yes, sign out every other device
            </Button>
            <Button type="button" variant="ghost" className="min-h-12 w-full" onClick={() => setConfirmReport(false)}>
              Not now
            </Button>
          </div>
        ) : (
          <Button type="button" variant="outline" className="mt-4 min-h-12 w-full" onClick={() => setConfirmReport(true)}>
            Secure my account
          </Button>
        )}
        <Link to="/profile" className="mt-3 inline-block text-sm text-[var(--gold-deep)] underline underline-offset-4">
          Change my password
        </Link>
      </section>


      <p className="mt-7 text-xs leading-relaxed text-muted-foreground">
        Your password is stored only as an irreversible encrypted value. Nobody at Recovery
        Trauma™ — including the founder — can see it, and no part of the admin area can display
        it. We never block an account because your internet connection or location changes.
      </p>
    </main>
  );
}
